import React, { useEffect, useState, useCallback } from 'react';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import StatusBanner from '../ui/StatusBanner';
import Avatar from '../ui/Avatar';
import SkeletonBlock from '../ui/SkeletonBlock';
import { StatusDot } from '../ui/Status';
import * as coachingApi from '../../services/coachingApi';
import { COACHING_UPDATED_EVENT } from '../../utils/directorEvents';
import './CoachingLog.css';

/**
 * Manager Scorecard "Coaching log" section.
 *
 * Shows the last N notes for a manager with their action items inline.
 * Inline editor lets the director write a new note + action items in
 * 30 seconds without leaving the page.
 *
 * Permissions are enforced server-side; the UI shows write affordances
 * to anyone who has access to the page (the auth middleware already
 * gated /location-scorecard for directors / admins).
 */
function CoachingLog({ manager, currentUser, period }) {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isWriting, setIsWriting] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const managerId = Number(manager?.managerDbId || manager?.id);
  const refresh = useCallback(async () => {
    if (!Number.isFinite(managerId) || managerId <= 0) {
      setNotes([]);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const list = await coachingApi.listNotesForManager(managerId, { limit: 25 });
      setNotes(list);
    } catch (err) {
      setError(err.message || 'Unable to load coaching log.');
    } finally {
      setIsLoading(false);
    }
  }, [managerId]);

  useEffect(() => {
    refresh();
    // B11 — refresh when any coaching mutation broadcasts (covers the
    // cross-tab / cross-role completion case).
    const handler = () => { refresh(); };
    window.addEventListener(COACHING_UPDATED_EVENT, handler);
    return () => window.removeEventListener(COACHING_UPDATED_EVENT, handler);
  }, [refresh]);

  const periodAnchor = period === 'yearly'
    ? `${new Date().getUTCFullYear()}`
    : (() => {
      const d = new Date();
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    })();

  const handleSubmit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await coachingApi.createNote({
        managerId,
        body: trimmed,
        periodAnchor
      });
      setDraft('');
      setIsWriting(false);
      await refresh();
    } catch (err) {
      setError(err.message || 'Unable to save the note.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleAction = async (item, nextStatus) => {
    try {
      await coachingApi.updateActionItem(item.id, { status: nextStatus });
      await refresh();
    } catch (err) {
      setError(err.message || 'Unable to update the action item.');
    }
  };

  if (!managerId) {
    return null;
  }

  return (
    <section className="coaching-log" aria-label="Coaching log">
      <div className="coaching-log-head">
        <div>
          <h2 className="coaching-log-title">Coaching log</h2>
          <p className="coaching-log-subtitle">
            Notes and action items from your 1:1s — operational coaching only.
          </p>
        </div>
        {!isWriting ? (
          <Button size="sm" variant="secondary" onClick={() => setIsWriting(true)}>
            + New note
          </Button>
        ) : null}
      </div>

      {error ? (
        <StatusBanner kind="error" message={error} onRetry={refresh} retryLabel="Retry" />
      ) : null}

      {isWriting ? (
        <div className="coaching-log-editor">
          <label htmlFor="coaching-note-body" className="coaching-log-editor-label">
            Coaching note · {periodAnchor}
          </label>
          <textarea
            id="coaching-note-body"
            className="coaching-log-editor-textarea"
            placeholder="What did you and the manager talk about? What's the next move?"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={4}
            maxLength={4000}
            disabled={submitting}
          />
          <div className="coaching-log-editor-actions">
            <Button
              size="sm"
              variant="primary"
              onClick={handleSubmit}
              disabled={!draft.trim() || submitting}
              loading={submitting}
              loadingLabel="Saving…"
            >
              Save note
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setIsWriting(false); setDraft(''); }}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {isLoading && !notes.length ? (
        <CoachingLogSkeleton />
      ) : notes.length ? (
        <ul className="coaching-log-notes">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              currentUser={currentUser}
              onToggleAction={handleToggleAction}
            />
          ))}
        </ul>
      ) : !isLoading ? (
        <EmptyState
          icon="·"
          headline="No coaching notes yet"
          body="Capture your next 1:1 here — what you agreed to do, due dates, who owns it."
          compact
        />
      ) : null}
    </section>
  );
}

function CoachingLogSkeleton() {
  return (
    <div className="coaching-log-skeleton" aria-label="Loading…" aria-live="polite">
      {Array.from({ length: 3 }).map((_, index) => (
        <div className="coaching-log-skeleton-note" key={index}>
          <div className="coaching-log-note-head">
            <SkeletonBlock width={24} height={24} radius="var(--radius-pill)" />
            <SkeletonBlock width={72} height={12} />
            <SkeletonBlock width={140} height={12} />
            <SkeletonBlock width={84} height={12} style={{ marginLeft: 'auto' }} />
          </div>
          <SkeletonBlock width="92%" height={14} />
          <SkeletonBlock width="68%" height={14} style={{ marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}

function NoteRow({ note, currentUser, onToggleAction }) {
  const created = note.createdAt ? new Date(note.createdAt) : null;
  const dateLabel = created
    ? created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  const author = note.authorManagerName || note.authorEmail || 'Unknown';
  const items = Array.isArray(note.actionItems) ? note.actionItems : [];

  return (
    <li className="coaching-log-note">
      <div className="coaching-log-note-head">
        <Avatar
          email={note.authorEmail}
          identifier={note.authorUserId || note.authorEmail || author}
          name={author}
          size="sm"
          userId={note.authorUserId}
        />
        <span className="coaching-log-note-date">{dateLabel}</span>
        <span className="coaching-log-note-author">by {author}</span>
        {note.periodAnchor ? (
          <span className="coaching-log-note-period">Period {note.periodAnchor}</span>
        ) : null}
      </div>
      <div className="coaching-log-note-body">{note.body}</div>
      {items.length ? (
        <ul className="coaching-log-action-list">
          {items.map((item) => (
            <ActionItemRow
              key={item.id}
              item={item}
              currentUser={currentUser}
              onToggle={onToggleAction}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function ActionItemRow({ item, currentUser, onToggle }) {
  const userId = Number(currentUser?.id);
  const canToggle = userId && (userId === item.ownerUserId || userId === item.createdByUserId);
  const checked = item.status === 'done';
  const skipped = item.status === 'skipped';
  const dueLabel = item.dueDate
    ? `due ${new Date(item.dueDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : null;
  const overdue = item.dueDate && item.status === 'open'
    && new Date(item.dueDate + 'T00:00:00').getTime() < Date.now() - 86400000;

  const tone = checked ? 'good'
    : skipped ? 'neutral'
    : overdue ? 'behind'
    : 'watch';

  const handleClick = () => {
    if (!canToggle) return;
    onToggle(item, checked ? 'open' : 'done');
  };

  return (
    <li className={`coaching-log-action-row ${item.status}`}>
      <button
        type="button"
        role="checkbox"
        className="coaching-log-action-checkbox"
        onClick={handleClick}
        disabled={!canToggle}
        aria-label={checked ? 'Mark open' : 'Mark done'}
        aria-checked={checked}
      >
        <span aria-hidden>{checked ? '☑' : skipped ? '–' : '☐'}</span>
      </button>
      <StatusDot tone={tone} className="coaching-log-action-dot" aria-hidden />
      <div className="coaching-log-action-body">
        <div className={`coaching-log-action-title ${checked ? 'is-done' : ''}`}>
          {item.title}
        </div>
        {item.body ? <div className="coaching-log-action-sub">{item.body}</div> : null}
      </div>
      <div className="coaching-log-action-meta">
        {dueLabel ? <span className={`coaching-log-action-due ${overdue ? 'is-overdue' : ''}`}>{dueLabel}</span> : null}
        {checked ? <span className="coaching-log-action-status">done</span> : null}
        {skipped ? <span className="coaching-log-action-status">skipped</span> : null}
      </div>
    </li>
  );
}

export default CoachingLog;
